import React, { createContext, useContext, useState, useCallback } from 'react';
import FeedbackToast from '../components/common/FeedbackToast';

const FeedbackContext = createContext();

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showFeedback = useCallback(
    ({ type = 'info', message, description = null, duration = 5000, action = null }) => {
      const id = Date.now() + Math.random();
      const toast = { id, type, message, description, duration, action };
      
      setToasts((prev) => [...prev, toast]);

      return id;
    },
    []
  );

  const showSuccess = useCallback(
    (message, description = null, action = null) => {
      return showFeedback({ type: 'success', message, description, action });
    },
    [showFeedback]
  );

  const showError = useCallback(
    (message, description = null, action = null) => {
      return showFeedback({ type: 'error', message, description, duration: 7000, action });
    },
    [showFeedback]
  );

  const showWarning = useCallback(
    (message, description = null, action = null) => {
      return showFeedback({ type: 'warning', message, description, duration: 6000, action });
    },
    [showFeedback]
  );

  const showInfo = useCallback(
    (message, description = null, action = null) => {
      return showFeedback({ type: 'info', message, description, action });
    },
    [showFeedback]
  );

  const removeFeedback = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const value = {
    showFeedback,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeFeedback,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      
      <div className="fixed top-20 right-4 z-50 space-y-4 pointer-events-none">
        <div className="space-y-2 pointer-events-auto">
          {toasts.map((toast) => (
            <FeedbackToast
              key={toast.id}
              type={toast.type}
              message={toast.message}
              description={toast.description}
              duration={toast.duration}
              action={toast.action}
              onClose={() => removeFeedback(toast.id)}
            />
          ))}
        </div>
      </div>
    </FeedbackContext.Provider>
  );
};
